import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';

/**
 * Reusable Base Modal Component
 * - Mobile-friendly with proper viewport handling
 * - Scrollable content area
 * - Customizable size and styling
 *
 * Rendered via a portal to document.body — not inline where each caller
 * happens to mount it. Several section wrappers in this app (e.g.
 * PricingPlansSection's own `<section>`) use `position: relative` +
 * an explicit `z-index`, which creates a CSS stacking context. A
 * `position: fixed` descendant escapes normal document FLOW but does
 * NOT escape an ancestor's stacking context for paint-order purposes —
 * so without a portal, this entire modal (including its close button)
 * was confined to compete for stacking only within whatever section
 * happened to render it, which could lose to unrelated fixed elements
 * elsewhere on the page (e.g. the navbar) regardless of this modal's
 * own much higher z-index numbers. A portal sidesteps the whole
 * problem by mounting outside every such ancestor, directly under
 * <body>, where z-[100]/z-[110] are finally compared at the real
 * document root — the only place a modal's z-index can be trusted to
 * mean what it says.
 */
const BaseModal = ({
  isOpen,
  onClose,
  children,
  maxWidth = 'max-w-4xl',
  showCloseButton = true,
  closeButtonClass = ''
}) => {
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm overflow-y-auto"
        onClick={onClose}
      >
        {/* Close Button — deliberately a SIBLING of the animated card
            below, not a child of it. CSS `position: fixed` is fixed
            relative to the nearest ancestor with a `transform` (or
            filter/perspective/will-change:transform) applied, not
            necessarily the real viewport — and Framer Motion animates
            the card below via `transform` (scale/y), which silently
            turned a `fixed` button inside it back into something that
            behaves like `absolute` and still scrolled away with the
            card's own content on a real mobile browser. This outer
            wrapper only animates opacity (no transform), so a `fixed`
            child of *this* element is genuinely fixed to the viewport. */}
        {showCloseButton && (
          <button
            onClick={onClose}
            className={`fixed top-4 right-4 sm:top-6 sm:right-6 text-white/60 hover:text-white transition-colors z-[110] p-2 bg-black/40 backdrop-blur-sm rounded-full ${closeButtonClass}`}
            aria-label="Close modal"
          >
            <FaTimes size={24} />
          </button>
        )}

        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className={`glass-strong rounded-3xl p-6 sm:p-8 w-full ${maxWidth} my-auto relative`}
          style={{
            maxHeight: 'calc(100vh - 2rem)',
            overflowY: 'auto'
          }}
        >
          {/* Modal Content */}
          <div className="relative">
            {children}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default BaseModal;
